import { Component, computed, effect, ElementRef, input, output, signal } from '@angular/core';
import { AnalogOut, ModuleBijzPrg, ModuleParam, RangedKanaalInput } from '../PropTypes';
import { KopConfigService } from '../../core/kop-config.service';
import { JSBridgeService } from '../../core/jsbridge.service';
import { KopModulesService } from '../../modules/kop-modules.service';
import { wasmVectorToArray } from '../../utils/wasmVector';
import { KanaalService } from '../../kanaal/kanaal.service';
import { sBijzPrg } from '@wasm/KopWeb';
import { TranslateService } from '../../i18n/translate.service';
import { kanaalLabelHdw } from '../../kanaal/kanaal.utils';
import { BaseElementComponent } from '../../keynav/base-element/base-element.component';
import { FocusManagerService } from '../../keynav/focus-manager.service';

@Component({
  selector: 'bijz-prgms-editor',
  templateUrl: './bijz-prgms-editor.component.html',
  styleUrl: './bijz-prgms-editor.component.scss'
})
export class BijzPrgmsEditorComponent extends BaseElementComponent {


  constructor(public kopConfigService: KopConfigService, public jsBridgeService: JSBridgeService,
    public kopModulesService: KopModulesService, public kanaalService: KanaalService, private translate: TranslateService,
    elementRef: ElementRef<HTMLElement>, focusManager: FocusManagerService) {
      super(elementRef, focusManager);
      this.keyProperty = "bijz-prgms-editor";
    effect(() => {
      let initValue = this.initialValue();
      if (initValue != undefined) {
        this.editValue = { ...initValue };
        let meta = this.meta();
        //If params are missing, fill them with default values
        if (meta != undefined) {
          let params = this.kopConfigService.buildDefaultCKParams(meta.param);
          for (let i = 0; i < 4; i++) {
            if (this.editValue.params[i] == undefined && params[i] != undefined) {
              this.editValue.params[i] = params[i];
            }
          }
        }
      }
    });
  }
  public isNew = input<boolean>(false);
  public editOnly = input<boolean>(false);
  public valueChanged = output<ModuleBijzPrg>();
  public onlyContents = input<boolean>(false);
  public onlyDesc = input<boolean>(false);


  public initialValue = input<ModuleBijzPrg>();

  public editValue: ModuleBijzPrg = {
    index: 0,
    params: {}
  };


  public newBPOptions = computed(() => {
    if (this.isNew()) {
      let bpMetas = this.kopConfigService.currentMeta().bijzPrgs;
      let selectedModule = this.kopModulesService.selectedAfdModule();
      let ret = bpMetas.filter(sm => {
        if (selectedModule != undefined) {
          if (selectedModule.meta.allBijzPrgm == undefined) {
            return false;
          }
          if (selectedModule.meta.allBijzPrgm.indexOf(sm.index) < 0) {
            return false;
          }
        }
        return true;
      });
      return ret;
    }
    return [];
  });

  alreadySelectedFN() {
    let tmBPs = this.kopConfigService.selectedAfd()?.afdeling().bijzPrgs;
    if (tmBPs != undefined) {
      let bps = wasmVectorToArray(tmBPs);
      let ret = (idx: number) => {
        return bps.find(a => a.index == idx) != undefined;
      }
      return ret;
    }
    return (idx: number) => false;
  }

  labelFn() {
    let ret = (meta: any) => {
      return this.translate.translateKopData('bijzprgm', (meta as sBijzPrg).index, (meta as sBijzPrg).omschr.toString());
    };
    return ret;
  }



  private _metaIdx = signal<number | undefined>(undefined);

  public metaIdx = input<number | undefined>(undefined);

  private newMetaIdx = computed(() => {
    if (this._metaIdx() != undefined) {
      return this._metaIdx();
    }
    return this.metaIdx();
  });

  public meta = computed(() => {
    let bp = this.initialValue();
    if (bp != undefined) {
      let meta = this.kopConfigService.currentMeta();
      return meta.bijzPrgs.find(m => m.index == bp.index);
    }
    let chosenMeta = this.newMetaIdx();
    if (chosenMeta != undefined) {
      let meta = this.kopConfigService.currentMeta();
      return meta.bijzPrgs.find(m => m.index == chosenMeta);
    }
  });

  public paramsMeta = computed(() => {
    let meta = this.meta();
    let afd = this.kopConfigService.selectedAfd();
    if (meta != undefined && afd != undefined) {
      let pmetas = wasmVectorToArray(meta.param);
      let ret = pmetas.map(pmeta => {
        return {
          pmeta: pmeta,
          assign: this.kanaalService.buildAssignementInfo('bijzPrg', afd?.afdeling(), meta, pmeta.no)
        }
      });
      return ret;
    }
    return [];
  });


  servoKCTypeLabel(sp: ModuleParam) {
    let kanaalType = this.kanaalService.getKCParamType(sp);
    if (kanaalType == undefined) {
      return "Parameter";
    }
    return `${kanaalType?.ioType.type} ${kanaalType?.ioType.direction}`;
  }


  public onParamKanaalChange(event: RangedKanaalInput, pNo: number) {
    this.editValue.params[pNo].inverted = event.inverted == true;
    this.editValue.params[pNo].waarde = event.start;
  }


  onNew(e: { index: number }) {
    let idx = e.index;
    this._metaIdx.set(idx);
    this.editValue = this.kopConfigService.buildDefaultBijzPrgm(idx);
    this.open();
  }


  onDelete() {
    this.close();
    let bp = this.initialValue();
    if (bp != undefined) {
      this.kopConfigService.eraseBijzPrgm(bp);
    }
  }

  onSave() {
    this.close();
    if (this.editOnly()) {
      this.valueChanged.emit({ ...this.editValue });
      return;
    }

    let meta = this.meta();
    if (meta != undefined) {
      this.kopConfigService.upsertBijzPrgm(this.editValue, meta);
    }
  }

  public components = computed(() => {
    let ms = this.initialValue();
    if (ms == undefined) {
      return undefined;
    }
    return this.bijPrgColumns(ms);
  });

  public bijPrgColumns(s: ModuleBijzPrg) {
    let servosMetas = this.kopConfigService.currentMeta().servos;
    let meta = servosMetas.find(m => m.index == s.index);
    let params = [];
    for (let i = 0; i < 4; i++) {
      let param = s.params[i];
      if (param != undefined) {
        let type = this.kanaalService.getIOTypeFromParamType(param?.type);
        if (type != undefined) {
          let puit = kanaalLabelHdw(param.waarde, type);
          puit = puit + (param.inverted ? '!' : '');
          params.push(puit);
        } else {
          params.push(`pa: ${param.waarde}`);
        }
      } else {
        params.push(``);
      }
    }
    return {
      index: s.index,
      omschrijving: meta?.omschr.toString(),
      params: params
    }
  }
}
